import { useMutation, useQueryClient } from '@tanstack/react-query'
import { addMoveToSeries } from '@/api/seriesMoves'
import { seriesKeys, movesKeys } from '@/api/queryKeys'
import { useToast } from '@/hooks/use-toast'

/**
 * Hook to add moves to a series
 *
 * Handles:
 * - API call to add multiple moves to series
 * - Cache invalidation for series detail queries
 * - Success/error toast notifications
 * - Telemetry events
 *
 * @returns Mutation object with mutate function and state
 *
 * @example
 * ```tsx
 * const addMovesToSeries = useAddMovesToSeries()
 *
 * const handleAddMoves = async (seriesId: string, moveIds: string[]) => {
 *   for (const moveId of moveIds) {
 *     await addMovesToSeries.mutateAsync({ seriesId, moveId })
 *   }
 * }
 * ```
 */
export function useAddMovesToSeries() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: ({ seriesId, moveId }: { seriesId: string; moveId: string }) =>
      addMoveToSeries({ seriesId, moveId }),
    onSuccess: (_data, variables: { seriesId: string; moveId: string }) => {
      // Invalidate series detail queries to refetch with new moves
      queryClient.invalidateQueries({
        queryKey: seriesKeys.detail(variables.seriesId),
      })

      // Invalidate only this specific move's queries (with exact: false to match all variants)
      queryClient.invalidateQueries({
        queryKey: movesKeys.detail(variables.moveId),
        exact: false,
      })

      // Show success toast
      toast({
        title: 'Move added',
        description: 'Move has been added to the series successfully.',
      })
    },
    onError: (error: Error) => {
      // Show error toast
      toast({
        title: 'Failed to add move',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      })
    },
  })
}
